import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {startLoading, stopLoading} from "./loadingSlice";
import {WawiService} from "../../api/generated/services/WawiService";
import {addNotification} from "./notificationSlice";
import {t} from "i18next";
import type {WawiOrdersView} from "../../api/generated/models/WawiOrdersView";
import {helper} from "../../utils/helper";

export interface ILieferbarOrder {
    shippingType: string;
    pendingCount: number;
}

export interface ILieferbarSlice {
    isConnected: boolean;
    /** Set when `/wawi/orders` returns 403 `subscription_upgrade_required`. */
    upgradeRequired: boolean;
    totalCount?: number | undefined;
    orders: ILieferbarOrder[];
}

/** rejectWithValue payload distinguishing a plan-gate 403 from other failures. */
interface LieferbarRejectPayload {
    upgradeRequired: boolean;
}

const initialState: ILieferbarSlice = {
    isConnected: true,
    upgradeRequired: false,
    totalCount: 0,
    orders: [],
};

export const getLieferbarOrders = createAsyncThunk<
    WawiOrdersView,
    void,
    { rejectValue: LieferbarRejectPayload }
>(
    "lieferbar/getLieferbarOrders",
    async (_, {dispatch, rejectWithValue}) => {
        dispatch(startLoading());

        try {
            const response: WawiOrdersView = await WawiService.getAppWawiOrders();
            return response;
        } catch (e) {
            // Plan-gate rejection: no error toast - the page shows the Upgrade CTA.
            if (helper.isUpgradeRequiredError(e)) {
                return rejectWithValue({upgradeRequired: true});
            }
            dispatch(addNotification({
                type: "error",
                title: "",
                message: t('lieferbar_get_orders_notification'),
            }))

            return rejectWithValue({upgradeRequired: false});
        } finally {
            dispatch(stopLoading());
        }
    }
);

export const lieferbarSlice = createSlice({
    initialState,
    name: "lieferbar",
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getLieferbarOrders.fulfilled, (state, action) => {
                state.totalCount = action.payload?.totalCount ?? 0;
                state.orders = action.payload?.orders ?? [];
                state.isConnected = true;
                state.upgradeRequired = false;
            })
            .addCase(getLieferbarOrders.rejected, (state, action) => {
                const upgradeRequired = action.payload?.upgradeRequired === true;
                state.upgradeRequired = upgradeRequired;
                // Stop the polling loop on any failure (page gates re-fetch).
                state.isConnected = false;
            })
    }
})

export default lieferbarSlice.reducer;
